import React, { useState, useEffect } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Plus,
  Settings,
  Eye,
  EyeOff,
  ChevronUp,
  ChevronDown,
  Trash2,
  Link as LinkIcon,
  X
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import './QuickLinksSidebar.css';

interface QuickLink {
  id: string;
  type: 'site' | 'personal';
  name: string;
  url: string;
  description: string | null;
  icon_url: string | null;
  display_order: number;
  is_hidden: boolean;
}

interface QuickLinksResponse {
  links: QuickLink[];
  hiddenLinks: QuickLink[];
  hiddenCount: number;
}

interface QuickLinksSidebarProps {
  onCollapseChange?: (collapsed: boolean) => void;
}

const QuickLinksSidebar: React.FC<QuickLinksSidebarProps> = ({ onCollapseChange }) => {
  const { currentSite } = useAuth();
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const saved = localStorage.getItem('quickLinksSidebarCollapsed');
    return saved === 'true';
  });
  const [links, setLinks] = useState<QuickLink[]>([]);
  const [hiddenLinks, setHiddenLinks] = useState<QuickLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [isManaging, setIsManaging] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newLink, setNewLink] = useState({ name: '', url: '', description: '' });
  const [addingLink, setAddingLink] = useState(false);

  useEffect(() => {
    if (currentSite) {
      fetchQuickLinks();
    }
  }, [currentSite]);

  useEffect(() => {
    onCollapseChange?.(isCollapsed);
  }, [isCollapsed, onCollapseChange]);

  const fetchQuickLinks = async () => {
    if (!currentSite) return;
    setLoading(true);
    try {
      const response = await api.get<QuickLinksResponse>('/api/my-quick-links');
      setLinks(response.data.links);
      setHiddenLinks(response.data.hiddenLinks);
    } catch (error) {
      console.error('Error fetching quick links:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleCollapse = () => {
    const newState = !isCollapsed;
    setIsCollapsed(newState);
    localStorage.setItem('quickLinksSidebarCollapsed', String(newState));
  };

  const handleAddLink = async () => {
    if (!newLink.name || !newLink.url) return;

    setAddingLink(true);
    try {
      // Ensure URL has protocol
      let url = newLink.url;
      if (!url.startsWith('http://') && !url.startsWith('https://')) {
        url = 'https://' + url;
      }

      await api.post('/api/my-quick-links', {
        name: newLink.name,
        url,
        description: newLink.description || null,
        display_order: links.length // Add at the end
      });

      setNewLink({ name: '', url: '', description: '' });
      setShowAddModal(false);
      fetchQuickLinks();
    } catch (error) {
      console.error('Error adding link:', error);
      alert('Failed to add link. Please try again.');
    } finally {
      setAddingLink(false);
    }
  };

  const handleDeleteLink = async (link: QuickLink) => {
    if (link.type !== 'personal') return;
    if (!window.confirm(`Delete "${link.name}"?`)) return;

    try {
      await api.delete(`/api/my-quick-links/${link.id}`);
      fetchQuickLinks();
    } catch (error) {
      console.error('Error deleting link:', error);
    }
  };

  const handleToggleVisibility = async (link: QuickLink) => {
    try {
      await api.post(`/api/my-quick-links/toggle-visibility/${link.id}`, {
        is_hidden: !link.is_hidden
      });
      fetchQuickLinks();
    } catch (error) {
      console.error('Error toggling visibility:', error);
    }
  };

  const handleMoveLink = async (index: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= links.length) return;

    const newLinks = [...links];
    [newLinks[index], newLinks[newIndex]] = [newLinks[newIndex], newLinks[index]];

    // Update display orders
    const order = newLinks.map((link, i) => ({
      id: link.id,
      type: link.type,
      display_order: i
    }));

    // Optimistic update
    setLinks(newLinks.map((link, i) => ({ ...link, display_order: i })));

    try {
      await api.post('/api/my-quick-links/reorder', { order });
    } catch (error) {
      console.error('Error reordering links:', error);
      fetchQuickLinks(); // Revert on error
    }
  };

  const handleRestoreLink = async (link: QuickLink) => {
    try {
      await api.post(`/api/my-quick-links/toggle-visibility/${link.id}`, {
        is_hidden: false
      });
      fetchQuickLinks();
    } catch (error) {
      console.error('Error restoring link:', error);
    }
  };

  return (
    <>
      <aside className={`quick-links-sidebar ${isCollapsed ? 'collapsed' : ''}`}>
        {/* Header with collapse toggle */}
        <div className="sidebar-header">
          {!isCollapsed && <span className="sidebar-title">Quick Links</span>}
          <button
            onClick={handleToggleCollapse}
            className="collapse-btn"
            title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {isCollapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
          </button>
        </div>

        {/* Links list */}
        <div className="sidebar-content">
          {loading ? (
            <div className="sidebar-loading">
              {!isCollapsed && <span>Loading...</span>}
            </div>
          ) : links.length === 0 && !isCollapsed ? (
            <div className="sidebar-empty">
              <p>No quick links yet</p>
            </div>
          ) : (
            <div className="links-list">
              {links.map((link, index) => (
                <div key={link.id} className="link-item-wrapper">
                  {isManaging && !isCollapsed && (
                    <div className="link-manage-controls">
                      <button
                        onClick={() => handleMoveLink(index, 'up')}
                        disabled={index === 0}
                        className="move-btn"
                        title="Move up"
                      >
                        <ChevronUp size={14} />
                      </button>
                      <button
                        onClick={() => handleMoveLink(index, 'down')}
                        disabled={index === links.length - 1}
                        className="move-btn"
                        title="Move down"
                      >
                        <ChevronDown size={14} />
                      </button>
                    </div>
                  )}
                  <a
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="link-item"
                    title={isCollapsed ? link.name : link.description || link.name}
                  >
                    <span className="link-icon">
                      {link.icon_url ? (
                        <img src={link.icon_url} alt="" className="link-favicon" />
                      ) : (
                        <LinkIcon size={16} />
                      )}
                    </span>
                    {!isCollapsed && (
                      <>
                        <span className="link-name">{link.name}</span>
                        <ExternalLink size={12} className="external-icon" />
                      </>
                    )}
                  </a>
                  {isManaging && !isCollapsed && (
                    <div className="link-actions">
                      {link.type === 'site' ? (
                        <button
                          onClick={() => handleToggleVisibility(link)}
                          className="action-btn hide-btn"
                          title="Hide this link"
                        >
                          <EyeOff size={14} />
                        </button>
                      ) : (
                        <button
                          onClick={() => handleDeleteLink(link)}
                          className="action-btn delete-btn"
                          title="Delete this link"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Hidden links section (only in manage mode) */}
          {isManaging && !isCollapsed && hiddenLinks.length > 0 && (
            <div className="hidden-links-section">
              <div className="hidden-links-header">
                Hidden ({hiddenLinks.length})
              </div>
              {hiddenLinks.map(link => (
                <div key={link.id} className="hidden-link-item">
                  <span className="link-name">{link.name}</span>
                  <button
                    onClick={() => handleRestoreLink(link)}
                    className="restore-btn"
                    title="Show this link"
                  >
                    <Eye size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer with actions */}
        {!isCollapsed && (
          <div className="sidebar-footer">
            <button
              onClick={() => setShowAddModal(true)}
              className="add-link-btn"
              title="Add a personal link"
            >
              <Plus size={16} />
              <span>Add Link</span>
            </button>
            <button
              onClick={() => setIsManaging(!isManaging)}
              className={`manage-btn ${isManaging ? 'active' : ''}`}
              title={isManaging ? 'Done managing' : 'Manage links'}
            >
              <Settings size={16} />
            </button>
          </div>
        )}

        {/* Collapsed state: just show add button */}
        {isCollapsed && (
          <div className="sidebar-footer-collapsed">
            <button
              onClick={() => {
                setIsCollapsed(false);
                localStorage.setItem('quickLinksSidebarCollapsed', 'false');
                setShowAddModal(true);
              }}
              className="add-link-btn-collapsed"
              title="Add a personal link"
            >
              <Plus size={18} />
            </button>
          </div>
        )}
      </aside>

      {/* Add Link Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="add-link-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Add Quick Link</h3>
              <button onClick={() => setShowAddModal(false)} className="modal-close">
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Name *</label>
                <input
                  type="text"
                  value={newLink.name}
                  onChange={e => setNewLink({ ...newLink, name: e.target.value })}
                  placeholder="My Link"
                  autoFocus
                />
              </div>
              <div className="form-group">
                <label>URL *</label>
                <input
                  type="text"
                  value={newLink.url}
                  onChange={e => setNewLink({ ...newLink, url: e.target.value })}
                  placeholder="https://example.com"
                />
              </div>
              <div className="form-group">
                <label>Description (optional)</label>
                <input
                  type="text"
                  value={newLink.description}
                  onChange={e => setNewLink({ ...newLink, description: e.target.value })}
                  placeholder="A brief description"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button onClick={() => setShowAddModal(false)} className="btn-cancel">
                Cancel
              </button>
              <button
                onClick={handleAddLink}
                disabled={!newLink.name || !newLink.url || addingLink}
                className="btn-add"
              >
                {addingLink ? 'Adding...' : 'Add Link'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default QuickLinksSidebar;
